import React from 'react';

/**
 * Animated swimlane sequence diagram, shared across corridors.
 *
 * Renders one vertical lifeline per actor and a horizontal message arrow per
 * step. The active step is highlighted; completed steps are dimmed; future
 * steps are faint. Pure presentational — parent owns the step index.
 *
 * @param {string[]} actors      Lane labels (left → right).
 * @param {object[]} steps        Each: { from, to, msgType, kind }.
 * @param {number}   activeIndex  Index of the highlighted step.
 * @param {function} onStepClick  Called with a step index when an arrow is clicked.
 */
const KIND_CLASS = {
  req:  { stroke: '#2563eb', pillBg: '#dbeafe', pillTx: '#1e40af' },
  ok:   { stroke: '#16a34a', pillBg: '#dcfce7', pillTx: '#166534' },
  warn: { stroke: '#d97706', pillBg: '#fef3c7', pillTx: '#92400e' },
  rjct: { stroke: '#dc2626', pillBg: '#fee2e2', pillTx: '#991b1b' },
};

export default function SequenceDiagram({ actors, steps, activeIndex, onStepClick }) {
  const laneX = actors.length === 3 ? [120, 340, 560] : actors.map((_, i) => 120 + i * 220);
  const topY = 60;
  const rowH = Math.min(58, (300 - topY) / Math.max(steps.length, 1));
  const bottomY = topY + rowH * steps.length + 12;

  return (
    <svg viewBox={`0 0 680 ${bottomY + 20}`} width="100%" role="img"
         aria-label="Message sequence diagram" className="select-none">
      {/* Actor headers + lifelines */}
      {actors.map((name, i) => (
        <g key={name}>
          <rect x={laneX[i] - 66} y={10} width={132} height={36} rx={8}
                fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
          <text x={laneX[i]} y={33} textAnchor="middle" fontSize="13"
                fontFamily="system-ui, sans-serif" fill="#0f172a">{name}</text>
          <line x1={laneX[i]} y1={46} x2={laneX[i]} y2={bottomY}
                stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 4" />
        </g>
      ))}

      {/* Message arrows */}
      {steps.map((s, i) => {
        const y = topY + rowH * i + rowH / 2;
        const x1 = laneX[s.from];
        const x2 = laneX[s.to];
        const active = i === activeIndex;
        const done = i < activeIndex;
        const opacity = active ? 1 : done ? 0.5 : 0.18;
        const c = KIND_CLASS[s.kind] || KIND_CLASS.req;
        const dir = x2 > x1 ? 1 : -1;
        const headX = x2 - dir * 7;
        const midX = (x1 + x2) / 2;
        const self = x1 === x2;
        // Pill sized to the label so longer labels (e.g. "POST /transfers") fit.
        const pillW = Math.max(84, (s.msgType?.length || 0) * 7 + 18);

        if (self) {
          // Self-message (e.g. an internal step on one lane): small loop marker.
          return (
            <g key={i} style={{ cursor: 'pointer' }} onClick={() => onStepClick && onStepClick(i)}>
              <rect x={x1 + 6} y={y - 9} width={7} height={14} fill="none" stroke={c.stroke} strokeWidth={active ? 1.6 : 1} opacity={opacity} />
              <text x={x1 + 20} y={y + 1} fontSize="12" fontFamily="ui-monospace, monospace" fill={c.pillTx} opacity={opacity}>{s.msgType}</text>
              {active && <circle cx={x1} cy={y} r={4} fill={c.stroke} />}
            </g>
          );
        }

        return (
          <g key={i} style={{ cursor: 'pointer' }} onClick={() => onStepClick && onStepClick(i)}>
            <rect x={Math.min(x1, x2)} y={y - 24} width={Math.abs(x2 - x1)} height={30} fill="transparent" />
            <line x1={x1} y1={y} x2={headX} y2={y} stroke={c.stroke}
                  strokeWidth={active ? 2.5 : 1.5} opacity={opacity} />
            <path d={`M${x2},${y} L${headX},${y - 5} L${headX},${y + 5} Z`} fill={c.stroke} opacity={opacity} />
            <rect x={midX - pillW / 2} y={y - 22} width={pillW} height={18} rx={9} fill={c.pillBg} opacity={opacity} />
            <text x={midX} y={y - 9} textAnchor="middle" fontSize="12"
                  fontFamily="ui-monospace, monospace" fill={c.pillTx} opacity={opacity}>{s.msgType}</text>
            {active && <circle cx={x1} cy={y} r={4} fill={c.stroke} />}
          </g>
        );
      })}
    </svg>
  );
}
